import { RateLevel, VALID_RATE_LEVELS } from '../../../constants';

export interface SegmentUrlParams {
  segmentId: string;
  rateLevel: RateLevel;
  advertiserId?: string | null;
  partnerId?: string | null;
}

export interface UrlValidationResult {
  isValid: boolean;
  error?: string;
  params?: SegmentUrlParams;
}

/**
 * Converts lowercase rate level from URL to proper title case for API
 * @param rateLevel - The rate level from URL (lowercase)
 * @returns Title case rate level (System, Advertiser, Partner)
 */
export const normalizeRateLevelForApi = (rateLevel: string): RateLevel => {
  const normalized = rateLevel?.toLowerCase();

  switch (normalized) {
    case 'system':
      return RateLevel.SYSTEM;
    case 'advertiser':
      return RateLevel.ADVERTISER;
    case 'partner':
      return RateLevel.PARTNER;
    default:
      return RateLevel.SYSTEM;
  }
};

/**
 * Builds query string for segment detail URL
 * @param rateLevel - The rate level (system/advertiser/partner)
 * @param advertiserId - The advertiser ID (optional)
 * @param partnerId - The partner ID (optional)
 * @returns Query string (e.g., "rateLevel=advertiser&advertiserId=adv_456")
 */
export const buildSegmentDetailQueryParams = (
  rateLevel: string | RateLevel,
  advertiserId?: string | null,
  partnerId?: string | null
): string => {
  const params = new URLSearchParams();
  const normalizedRateLevel = normalizeRateLevelForApi(rateLevel);

  params.append('rateLevel', normalizedRateLevel);

  if (normalizedRateLevel === RateLevel.ADVERTISER && advertiserId) {
    params.append('advertiserId', advertiserId);
  } else if (normalizedRateLevel === RateLevel.PARTNER && partnerId) {
    params.append('partnerId', partnerId);
  }

  return params.toString();
};

/**
 * Parses and validates segment detail URL parameters
 * @param searchParams - URLSearchParams object from location.search
 * @param segmentId - The segment ID from URL path
 * @returns Validation result with parsed params or error
 */
export const parseAndValidateSegmentParams = (
  searchParams: URLSearchParams,
  segmentId: string
): UrlValidationResult => {
  const rateLevelFromUrl = searchParams.get('rateLevel');
  const advertiserId = searchParams.get('advertiserId') || undefined;
  const partnerId = searchParams.get('partnerId') || undefined;

  // Validate rateLevel
  if (!rateLevelFromUrl ||
    !VALID_RATE_LEVELS?.map((level) => level.toLowerCase()).includes(
      rateLevelFromUrl?.toLowerCase()
    )
  ) {
    return {
      isValid: false,
      error: `Invalid rate level. Must be one of: ${VALID_RATE_LEVELS.join(
        ', '
      )}`,
    };
  }

  // Normalize rate level from URL (lowercase) to proper title case for API
  const rateLevel = normalizeRateLevelForApi(rateLevelFromUrl);

  // Validate based on rateLevel
  if (rateLevel === RateLevel.ADVERTISER && !advertiserId) {
    return {
      isValid: false,
      error: 'Advertiser ID is required when rate level is "advertiser"',
    };
  }

  if (rateLevel === RateLevel.PARTNER && !partnerId) {
    return {
      isValid: false,
      error: 'Partner ID is required when rate level is "partner"',
    };
  }

  return {
    isValid: true,
    params: {
      segmentId,
      rateLevel,
      advertiserId,
      partnerId,
    },
  };
};

/**
 * Builds GraphQL variables for segment queries
 * @param segmentId - The segment ID
 * @param rateLevel - The rate level (will be normalized to title case)
 * @param advertiserId - The advertiser ID (optional)
 * @param partnerId - The partner ID (optional)
 * @returns GraphQL variables object
 */
export const buildSegmentQueryVariables = (
  segmentId: string,
  rateLevel: RateLevel | string,
  advertiserId?: string,
  partnerId?: string
) => {
  // Ensure rate level is in proper title case format for backend API
  const normalizedRateLevel =
    typeof rateLevel === 'string'
      ? normalizeRateLevelForApi(rateLevel)
      : rateLevel || RateLevel.SYSTEM;

  const variables: any = {
    segmentId,
    rateLevel: normalizedRateLevel,
  };

  if (normalizedRateLevel === RateLevel.ADVERTISER && advertiserId) {
    variables.advertiserId = advertiserId;
  } else if (normalizedRateLevel === RateLevel.PARTNER && partnerId) {
    variables.partnerId = partnerId;
  }

  return variables;
};
